<template>
  <div
    ref="rootEl"
    class="thsw"
    :class="{
      'is-dragging': dragging,
      'menu-right': menuRight,
      'menu-below': menuBelow,
    }"
    :style="rootStyle"
    :aria-label="t('common.theme.aria')"
  >
    <button
      type="button"
      class="thsw-tog"
      :title="t('common.theme.title')"
      :aria-expanded="menuOpen"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerUp"
      @lostpointercapture="onPointerUp"
      @click.stop="onToggle"
    >
      🎨
    </button>
    <div v-show="menuOpen" class="thsw-menu">
      <div class="thsw-t">{{ t('common.theme.menu') }}</div>
      <button
        v-for="opt in themeStore.options"
        :key="opt.id"
        type="button"
        class="thsw-opt"
        :class="{ on: themeStore.preset === opt.id }"
        @click="onPick(opt.id)"
      >
        <i :style="{ background: opt.swatch }" />
        {{ themeLabel(opt.id) }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onClickOutside } from '@vueuse/core'
import type { ThemePreset } from '~/stores/theme'

const DRAG_THRESHOLD = 6
const VIEW_PAD = 8
const MENU_W = 140
const MENU_H = 180

const themeStore = useThemeStore()
const { t } = useI18n()

const THEME_LABEL_KEY = {
  'dark-purple': 'common.theme.darkPurple',
  'dark-gold': 'common.theme.darkGold',
  'light-gold': 'common.theme.lightGold',
  'light-blue': 'common.theme.lightBlue',
} as const

const themeLabel = (id: ThemePreset) => t(THEME_LABEL_KEY[id])
const menuOpen = ref(false)
const rootEl = ref<HTMLElement | null>(null)
/** 距视口右、下边缘。未拖动前不写入，交给 CSS 钉在底栏上方 */
const pos = ref({ right: 10, bottom: 84 })
const placed = ref(false)
const dragging = ref(false)
const vw = ref(0)
const vh = ref(0)

let originRight = 0
let originBottom = 0
let startPx = 0
let startPy = 0
let activePointer = -1
let didDrag = false

onClickOutside(rootEl, () => {
  menuOpen.value = false
})

const togSize = () => {
  const tog = rootEl.value?.querySelector('.thsw-tog') as HTMLElement | null
  return {
    w: tog?.offsetWidth || 40,
    h: tog?.offsetHeight || 40,
  }
}

const clampPos = (right: number, bottom: number) => {
  const { w, h } = togSize()
  const maxRight = Math.max(VIEW_PAD, vw.value - w - VIEW_PAD)
  const maxBottom = Math.max(VIEW_PAD, vh.value - h - VIEW_PAD)
  return {
    right: Math.min(maxRight, Math.max(VIEW_PAD, right)),
    bottom: Math.min(maxBottom, Math.max(VIEW_PAD, bottom)),
  }
}

const syncViewport = () => {
  vw.value = window.innerWidth
  vh.value = window.innerHeight
}

const readOffset = () => {
  const r = rootEl.value!.getBoundingClientRect()
  return {
    right: window.innerWidth - r.right,
    bottom: window.innerHeight - r.bottom,
  }
}

const rootStyle = computed(() => {
  if (!placed.value) return undefined
  return {
    left: 'auto',
    top: 'auto',
    right: `${pos.value.right}px`,
    bottom: `${pos.value.bottom}px`,
  }
})

const menuRight = computed(() => !placed.value || pos.value.right < MENU_W)
const menuBelow = computed(() => {
  if (!placed.value) return false
  return vh.value - pos.value.bottom - togSize().h < MENU_H + 16
})

const onPointerDown = (e: PointerEvent) => {
  if (e.button !== 0) return
  const btn = e.currentTarget as HTMLElement
  btn.setPointerCapture(e.pointerId)
  activePointer = e.pointerId
  startPx = e.clientX
  startPy = e.clientY
  const origin = rootEl.value ? readOffset() : pos.value
  originRight = origin.right
  originBottom = origin.bottom
  didDrag = false
  dragging.value = false
}

const onPointerMove = (e: PointerEvent) => {
  if (e.pointerId !== activePointer) return
  const dx = e.clientX - startPx
  const dy = e.clientY - startPy
  if (!dragging.value) {
    if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return
    dragging.value = true
    didDrag = true
    placed.value = true
    menuOpen.value = false
  }
  e.preventDefault()
  pos.value = clampPos(originRight - dx, originBottom - dy)
}

const onPointerUp = (e: PointerEvent) => {
  if (e.pointerId !== activePointer) return
  activePointer = -1
  dragging.value = false
}

const onToggle = () => {
  if (didDrag) return
  menuOpen.value = !menuOpen.value
}

const onPick = (id: ThemePreset) => {
  themeStore.setTheme(id)
  menuOpen.value = false
}

const onResize = () => {
  syncViewport()
  if (!placed.value) return
  pos.value = clampPos(pos.value.right, pos.value.bottom)
}

onMounted(() => {
  syncViewport()
  localStorage.removeItem('theme-switch-pos')
  localStorage.removeItem('theme-switch-pos-br')
  localStorage.removeItem('theme-switch-pos-br2')
  localStorage.removeItem('theme-switch-pos-edge')
  window.addEventListener('resize', onResize)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize)
})
</script>
